import { ref } from 'vue'
import router from '../router'

export function useApi() {
  const isLoading = ref(false)
  const error = ref(null)

  const fetchApi = async (endpoint, options = {}) => {
    isLoading.value = true
    error.value = null

    try {
      const url = `${import.meta.env.VITE_API_BASE_URL || ''}${endpoint}`
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      })

      if (response.status === 401) {
        // SPA navigation — no full-page reload, preserves bfcache eligibility
        router.push('/login')
        throw new Error('Session expired')
      }

      if (!response.ok) {
        let detail = `HTTP error! status: ${response.status}`
        let errBody = null
        try {
          errBody = await response.json()
          if (errBody?.detail) detail = errBody.detail
        } catch {}
        // An admin change to a week whose payroll is already confirmed (payroll_calc.
        // ensure_pay_week_open): ask, then resend with the confirmation flag.
        if (errBody?.detail?.error_code === 'closed_pay_week') {
          if (window.confirm(errBody.detail.message)) {
            const sep = endpoint.includes('?') ? '&' : '?'
            return await fetchApi(`${endpoint}${sep}confirm_closed_week=1`, options)
          }
          detail = 'Change cancelled.'
        }
        const err = new Error(detail)
        err.detail = detail
        // Structured fields some error responses carry (e.g. stripe_connect_not_ready's
        // settings_url) — components can check err.errorCode for known cases instead
        // of string-matching err.detail.
        err.errorCode = errBody?.error_code || null
        err.body = errBody
        throw err
      }

      const data = await response.json()
      
      // Universal task event dispatch - notify listeners of any task creation
      if (data && data.task_id) {
        window.dispatchEvent(new CustomEvent('task-submitted', {
          detail: { taskId: data.task_id }
        }))
      }

      // Attendance marks, invoice confirms, signup responses and check-in answers change the dashboard
      // tab badges (useTeacherBadges.js) — tell it to refetch.
      if ((options.method || 'GET') !== 'GET' && /^\/api\/(lessons|teacher-signups|series-stops)\b/.test(endpoint)) {
        window.dispatchEvent(new Event('teacher-badges-stale'))
      }

      isLoading.value = false
      return data
    } catch (err) {
      isLoading.value = false
      error.value = err.message
      console.error('API error:', err)
      throw err
    }
  }

  /**
   * Fetch authenticated user's lessons timeline
   * @param {number} pastLimit - Number of past lessons to fetch (default: 10)
   * @param {number} futureLimit - Number of future lessons to fetch (default: 10)
   * @param {number} pastOffset - Offset for past lessons (default: 0)
   * @param {number} futureOffset - Offset for future lessons (default: 0)
   */
  const fetchUserLessonsTimeline = async (
    pastLimit = 10,
    futureLimit = 10,
    pastOffset = 0,
    futureOffset = 0
  ) => {
    const params = new URLSearchParams({
      past_limit: pastLimit,
      future_limit: futureLimit,
      past_offset: pastOffset,
      future_offset: futureOffset
    })
    
    return fetchApi(`/api/lessons/timeline?${params.toString()}`)
  }

  /**
   * Fetch monthly stats for current teacher (authenticated user)
   */
  const fetchCurrentMonthStats = async () => {
    return fetchApi('/api/stats/teacher/current-month')
  }

  /**
   * Mark lesson as attended
   * @param {number} lessonId - Lesson ID
   * @param {string} studentIdentifier - Student identifier (default: "Student")
   */
  const markLessonAttended = async (lessonId, studentIdentifier = 'Student') => {
    const response = await fetchApi(`/api/lessons/${lessonId}/update-attendance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        new_status: 'attended',
        student_identifier: studentIdentifier
      })
    })
    
    return response
  }

  /**
   * Mark lesson as excused
   * @param {number} lessonId - Lesson ID
   * @param {string} studentIdentifier - Student identifier (default: "Student")
   */
  const markLessonExcused = async (lessonId, studentIdentifier = 'Student') => {
    const response = await fetchApi(`/api/lessons/${lessonId}/update-attendance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        new_status: 'excused',
        student_identifier: studentIdentifier
      })
    })

    return response
  }

  /**
   * Bulk-cancel the current teacher's lessons in a date/time range (Teacher Cancellations).
   * @param {{ start_datetime: string, end_datetime: string, reason?: string }} body
   */
  const createTeacherCancellation = async (body, asTeacher) => {
    const qs = asTeacher ? `?as_teacher=${encodeURIComponent(asTeacher)}` : ''
    return await fetchApi(`/api/teacher-cancellations${qs}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
  }

  /**
   * Mark lesson as absent
   * @param {number} lessonId - Lesson ID
   * @param {string} studentIdentifier - Student identifier (default: "Student")
   */
  const markLessonAbsent = async (lessonId, studentIdentifier = 'Student') => {
    const response = await fetchApi(`/api/lessons/${lessonId}/update-attendance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        new_status: 'absent',
        student_identifier: studentIdentifier
      })
    })
    
    return response
  }

  /**
   * Mark lesson as not reported
   * @param {number} lessonId - Lesson ID
   * @param {string} studentIdentifier - Student identifier (default: "Student")
   */
  const markLessonNotReported = async (lessonId, studentIdentifier = 'Student') => {
    const response = await fetchApi(`/api/lessons/${lessonId}/update-attendance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        new_status: 'not reported',
        student_identifier: studentIdentifier
      })
    })
    
    return response
  }

  /**
   * Trigger MSM lessons sync
   * @returns {Promise} Task ID and status
   */
  const triggerMSMLessonsSync = async () => {
    return fetchApi('/api/sync/msm-lessons', {
      method: 'GET'
    })
  }

  const editLesson = async (lessonId, body) => {
    return fetchApi(`/api/lesson-edits/${lessonId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  const cloneLesson = async (lessonId, body) => {
    return fetchApi(`/api/lesson-edits/${lessonId}/clone`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  const cancelLesson = async (lessonId) => {
    return fetchApi(`/api/lesson-edits/${lessonId}/cancel`, {
      method: 'POST',
    })
  }

  const createLesson = async (body, asTeacher) => {
    const qs = asTeacher ? `?as_teacher=${encodeURIComponent(asTeacher)}` : ''
    return fetchApi(`/api/lessons/${qs}`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  const createLessonSeries = async (body) => {
    return fetchApi('/api/lesson-series/', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  const getSeriesRateForStudent = async ({ studentId, contactId, instrument, teacherId }) => {
    const params = new URLSearchParams()
    if (studentId) params.set('student_id', studentId)
    else if (contactId) params.set('contact_id', contactId)
    if (instrument) params.set('instrument', instrument)
    if (teacherId) params.set('teacher_id', teacherId)
    return fetchApi(`/api/lesson-series/rate-for-student?${params.toString()}`)
  }

  // The signup-specialist "inquiry" pipeline — used by the unified lesson-create
  // modal's admin/signups no-PM branch to reuse the existing teacher-request /
  // direct-invite infrastructure instead of a bespoke flow.
  const createSubmission = async (body) => {
    return fetchApi('/api/admin/submissions', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  const suggestSlots = async (submissionId, body) => {
    return fetchApi(`/api/admin/submissions/${submissionId}/suggest-slots`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  const sendInviteDirect = async (submissionId, body) => {
    return fetchApi(`/api/admin/submissions/${submissionId}/send-invite-direct`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  // One endpoint for every manual log — call / outside text / outside email / note
  // (backend: contacts.py log_interaction). Returns { inquiries: [...changed signup cards] }.
  const logInteraction = async (contactId, body) => {
    return fetchApi(`/api/contacts/${contactId}/interactions`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchApi,
    fetchUserLessonsTimeline,
    fetchCurrentMonthStats,
    markLessonAttended,
    markLessonExcused,
    createTeacherCancellation,
    markLessonAbsent,
    markLessonNotReported,
    triggerMSMLessonsSync,
    editLesson,
    cloneLesson,
    cancelLesson,
    createLesson,
    createLessonSeries,
    getSeriesRateForStudent,
    createSubmission,
    suggestSlots,
    sendInviteDirect,
    logInteraction,
  }
}